import { t } from 'i18next';
import { isNil } from 'lodash-es';
import * as yup from 'yup';

import { DEFAULT_MAX_LOADS_COUNT, SPLITTABLE_MAX_LOADS_COUNT } from '~constants/consts';
import { nameIdSchemaRequired } from '~constants/regexConst';
import {
  formQuantityFieldRepresentsEmptyQuantity,
  getStringOrNumberFieldPositiveNumberValue,
} from '~utils/utilFunctions';

const makeRequestedLoadsCountSchema = (isSplittable?: boolean) => {
  const maxLoadsCount = isSplittable
    ? SPLITTABLE_MAX_LOADS_COUNT
    : DEFAULT_MAX_LOADS_COUNT;

  return yup.lazy((value) => {
    const isEmpty = isNil(value) || String(value).trim() === '';

    if (isEmpty) {
      return yup.string().notRequired();
    }

    return yup
      .number()
      .integer(t('form_fields.must_be_integer'))
      .min(1, t('form_validation_errors.minimum_is_num', { num: 1 }))
      .max(
        maxLoadsCount,
        t('form_validation_errors.maximum_is_num', { num: maxLoadsCount }),
      )
      .typeError(t('form_validation_errors.required_simple'))
      .required(t('form_validation_errors.required_simple'));
  });
};

const requestedLoadsCountSchema = makeRequestedLoadsCountSchema();

const equipmentTypeGrossCapacitySchema = yup.lazy((value) => {
  const isEmpty = isNil(value) || String(value).trim() === '';

  if (isEmpty) {
    return yup.string().notRequired();
  }

  return yup
    .number()
    .min(0, t('form_validation_errors.minimum_is_num', { num: 0 }))
    .typeError(t('form_validation_errors.required_simple'))
    .required(t('form_validation_errors.required_simple'));
});

export const jobStartTimeSchema = yup.lazy((value) => {
  const isEmpty = isNil(value) || String(value).trim() === '';

  if (isEmpty) {
    return yup.string().notRequired();
  }

  return yup
    .date()
    .required(t('form_validation_errors.required_simple'))
    .typeError(t('form_validation_errors.invalid_time'));
});

export const jobQuantitySchema = yup.lazy((value) => {
  const isEmpty = isNil(value) || String(value).trim() === '';

  if (isEmpty) {
    return yup.string().notRequired();
  }

  return yup
    .number()
    .min(0.01, t('form_validation_errors.minimum_is_num', { num: 0.01 }))
    .typeError(t('form_validation_errors.required_simple'))
    .required(t('form_validation_errors.required_simple'));
});

const jobUnitOfMeasureSchema = nameIdSchemaRequired.notRequired();

const inlineJobRateSchema = yup
  .object()
  .shape({
    rate: yup.mixed<string | number>().nullable().notRequired(),
    type: yup
      .object()
      .shape({ label: yup.string().required(), value: yup.string().required() })
      .nullable()
      .notRequired()
      .default(null),
  })
  .nullable()
  .notRequired()
  .default(null);
export type InlineJobRateDto = yup.InferType<typeof inlineJobRateSchema>;

export function inlineJobRateHasValueWithoutType(rateField?: InlineJobRateDto) {
  const hasValue = !isNil(rateField?.rate) && String(rateField?.rate).trim() !== '';

  return hasValue && !rateField?.type?.value;
}

const editSingleJobTableRowSchema = yup.object().shape({
  requestedLoadsCount: requestedLoadsCountSchema,
  requestedLoadsCountInitialValue: requestedLoadsCountSchema,
  equipmentTypeGrossCapacity: equipmentTypeGrossCapacitySchema,
  equipmentTypeGrossCapacityInitialValue: equipmentTypeGrossCapacitySchema,
  jobStartTime: jobStartTimeSchema,
  recurringLoads: yup.boolean().required(),
  jobNotes: yup.string(),
  unitOfMeasure: jobUnitOfMeasureSchema,
  jobQuantity: jobQuantitySchema,
  orderUnitOfMeasure: nameIdSchemaRequired.notRequired(),
  orderQuantity: yup.number().notRequired(),
  material: nameIdSchemaRequired.notRequired().nullable().default(null),
  customerRate: inlineJobRateSchema,
  vendorRate: inlineJobRateSchema,
  driverRate: inlineJobRateSchema,
});
export type EditSingleJobTableRowDto = yup.InferType<typeof editSingleJobTableRowSchema>;

export type EditJobTableRowsDto = Record<string, EditSingleJobTableRowDto>;

export function makeEditJobTableRowsSchema(vendorJobSplittingEnabled?: boolean) {
  const loadsCountSchema = makeRequestedLoadsCountSchema(vendorJobSplittingEnabled);
  const rowSchema = editSingleJobTableRowSchema.shape({
    requestedLoadsCount: loadsCountSchema,
    requestedLoadsCountInitialValue: loadsCountSchema,
  });

  return yup.object().test({
    name: 'single-truck-shape',
    message: 'Invalid entry',
    test(value) {
      if (!value || typeof value !== 'object') {
        return false;
      }

      return Object.values(value).every((entry) => {
        const typedEntry = entry as EditSingleJobTableRowDto;

        const hasRateValueMissingItsType = [
          typedEntry.customerRate,
          typedEntry.vendorRate,
          typedEntry.driverRate,
        ].some(inlineJobRateHasValueWithoutType);

        if (hasRateValueMissingItsType) {
          return false;
        }

        const hasEmptyJobQuantity = formQuantityFieldRepresentsEmptyQuantity(
          typedEntry.jobQuantity,
        );
        const shouldRequireUoM = hasEmptyJobQuantity
          ? false
          : !typedEntry.unitOfMeasure?.id;

        if (shouldRequireUoM) {
          return false;
        }

        const hasEmptyOrderQuantity = formQuantityFieldRepresentsEmptyQuantity(
          typedEntry.orderQuantity,
        );
        const jobUomId = typedEntry.unitOfMeasure?.id;
        const orderUomId = typedEntry.orderUnitOfMeasure?.id;

        if (!hasEmptyJobQuantity && !hasEmptyOrderQuantity) {
          const jobQuantity = getStringOrNumberFieldPositiveNumberValue(
            typedEntry.jobQuantity ?? null,
          );
          const orderQuantity = getStringOrNumberFieldPositiveNumberValue(
            typedEntry.orderQuantity ?? null,
          );

          if (
            jobUomId &&
            jobUomId === orderUomId &&
            jobQuantity &&
            orderQuantity &&
            jobQuantity > orderQuantity
          ) {
            return false;
          }
        }

        return rowSchema.isValidSync(entry);
      });
    },
  });
}
